import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { db } from "../firebase";
import { getPartnerOrder } from "../utility/partnerCheckout";
import { downloadPDF } from "../utility/createDownloadTicket";
import { getEventThemeVars } from "../utility/colorUtils";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "./EventPartnerCheckout.scss";

export default function EventPartnerCheckout() {
  const { eventId, orderId, paymentStatus } = useParams();
  const [search] = useSearchParams();
  const token = search.get("token") || "";
  const { t } = useTranslation("eventdesc");
  const [event, setEvent] = useState(null);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    let active = true;
    getDoc(doc(db, "Events", eventId))
      .then((snapshot) => {
        if (active && snapshot.exists()) setEvent({ ...snapshot.data(), id: snapshot.id });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [eventId]);

  const refresh = useCallback(
    async (cancel = false) => {
      setBusy(true);
      try {
        const result = await getPartnerOrder({ eventId, orderId, token, cancel });
        setOrder(result);
        setError("");
      } catch (failure) {
        setError(failure.message || t("registration_failed"));
      } finally {
        setBusy(false);
      }
    },
    [eventId, orderId, token, t],
  );

  useEffect(() => {
    let active = true,
      timer;
    setOrder(null);
    setError("");
    setBusy(true);
    const poll = async (cancel = false) => {
      try {
        const result = await getPartnerOrder({ eventId, orderId, token, cancel });
        if (!active) return;
        setOrder(result);
        setError("");
        if (
          ["preparing", "pending", "refund_pending"].includes(result.status) ||
          (result.status === "paid" && result.fulfillmentStatus !== "complete")
        )
          timer = setTimeout(() => poll(), 10000);
      } catch (failure) {
        if (active) setError(failure.message || t("registration_failed"));
      } finally {
        if (active) setBusy(false);
      }
    };
    poll(paymentStatus === "canceled");
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [eventId, orderId, token, paymentStatus, t]);

  const download = async (participantId) => {
    setDownloading(participantId);
    try {
      const snapshot = await getDoc(doc(db, "Events", eventId, "Participants", participantId));
      if (!snapshot.exists() || !snapshot.data().paid) throw new Error(t("registration_failed"));
      await downloadPDF({ participant: { ...snapshot.data(), id: snapshot.id }, event });
    } catch (failure) {
      setError(failure.message);
    } finally {
      setDownloading(null);
    }
  };
  const title =
    ["refund_pending", "refunded", "refund_failed"].includes(order?.status)
      ? `partner_${order.status}`
      : order?.status === "paid"
      ? "partner_payment_confirmed"
      : ["expired", "failed"].includes(order?.status)
        ? "partner_payment_failed"
        : "partner_payment_pending";

  return (
    <div
      className={`event-ticketing ${event?.darkMode === false ? "light-mode" : ""}`}
      style={getEventThemeVars(event)}
    >
      <Header logoUrl={event?.logoUrl} isLoading={!event} />
      <main className="partner-order-confirmation container">
        <section>
          <h1>{t(title)}</h1>
          {event && <h2>{event.name}</h2>}
          {error && <p role="alert">{error}</p>}
          {["refund_pending", "refunded", "refund_failed"].includes(order?.status) && <p role="status">{t(`partner_${order.status}_description`)}</p>}
          {order?.status === "paid" && (
            <>
              <p>{t("partner_amount_paid", { amount: order.amount.toFixed(2) })}</p>
              {order.fulfillmentStatus !== "complete" && (
                <p role="status">{t("partner_delivery_pending")}</p>
              )}
              <ul>
                {order.participants.map((participant) => (
                  <li key={participant.id}>
                    <div>
                      {participant.details
                        .filter((field) => ["Firstname", "Name"].includes(field.name))
                        .map((field) => field.value)
                        .join(" ")}
                      <small>
                        {t(participant.emailSent ? "partner_email_sent" : "partner_email_pending")}
                      </small>
                    </div>
                    <button
                      type="button"
                      disabled={!event || downloading !== null}
                      onClick={() => download(participant.id)}
                    >
                      {t("download_tickets_here")}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
          <div className="partner-confirmation-actions">
            {order?.partnerId && (
              <Link to={`/events/${eventId}/partner/${order.partnerId}`}>
                {t("partner_return_link")}
              </Link>
            )}
            {(error || order?.fulfillmentStatus !== "complete") && (
              <button type="button" disabled={busy} onClick={() => refresh()}>
                {t("retry")}
              </button>
            )}
          </div>
          {busy && <p role="status">{t("partner_checking_payment")}</p>}
        </section>
      </main>
      <Footer logoUrl={event?.logoUrl} isDarkMode={event?.darkMode === false ? "light-mode" : ""} />
    </div>
  );
}
